import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layers, ArrowUp, ArrowDown, ClipboardList, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABEL = { inspection: 'Inspection', inspection_edit: 'Inspection edited', inspection_deleted: 'Inspection deleted', edit: 'Edit Hive' };

const fmt = (d) => {
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const ChangeChip = ({ c }) => {
  const up = c.to > c.from;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 ${up ? 'bg-lime-50 text-lime-800' : 'bg-rose-50 text-rose-700'}`}>
      <Icon className="h-3 w-3" />
      {up ? 'Added' : 'Removed'} {Math.abs(c.to - c.from)} {c.box} <span className="text-stone-500">({c.from} → {c.to})</span>
    </span>
  );
};

export const HiveStackHistory = ({ hiveId }) => {
  const [items, setItems] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('crewToken') || localStorage.getItem('token');
    axios.get(`${API}/hives/${hiveId}/stack-history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setItems(res.data.history || []))
      .catch(() => setItems([]));
  }, [hiveId]);

  if (items === null) return null;
  const visible = showAll ? items : items.slice(0, 5);

  return (
    <div data-testid="hive-stack-history">
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <h3 className="font-bold text-stone-800 flex items-center gap-2 mb-3">
          <Layers className="h-5 w-5 text-amber-600" /> Stack Change History
        </h3>
        {items.length === 0 ? (
          <p className="text-sm text-stone-500" data-testid="hive-stack-history-empty">No boxes added or removed yet. Changes from inspections and Edit Hive will show here.</p>
        ) : (
          <ol className="relative border-l-2 border-amber-200 ml-2 space-y-4">
            {visible.map(h => (
              <li key={h.id} className="pl-4 relative" data-testid={`stack-history-item-${h.id}`}>
                <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-amber-500" />
                <div className="flex items-center gap-2 text-xs text-stone-500">
                  {h.source === 'edit' ? <Pencil className="h-3 w-3" /> : <ClipboardList className="h-3 w-3" />}
                  {fmt(h.date || h.created_at)} · {SOURCE_LABEL[h.source] || h.source}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {h.changes.map(c => <ChangeChip key={c.box} c={c} />)}
                </div>
              </li>
            ))}
          </ol>
        )}
        {items.length > 5 && (
          <button onClick={() => setShowAll(v => !v)} className="mt-3 text-sm text-amber-700 font-medium hover:underline" data-testid="stack-history-toggle">
            {showAll ? 'Show less' : `Show all ${items.length}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default HiveStackHistory;
